import {
  Component,
  OnInit,
  signal,
  computed,
  effect,
  HostBinding,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';

import { ToolbarModule } from 'primeng/toolbar';
import { PanelMenuModule } from 'primeng/panelmenu';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { MenuItem, MessageService } from 'primeng/api';
import { WizardStepService } from '../../backoffice/services/wizard-step.service';
import { ProfessionalsWizardService } from '../../frontoffice/services/professionals-wizard.service';
import { RegistrationStep } from '../../core/services/registration-step.service';
import { ContractSectionsService } from '../../backoffice/services/contract-sections.service';
import { ChecklistService } from '../../backoffice/services/checklist.service';
import { ProfessionalsDataService } from '../../frontoffice/services/professionals-data.service';
import { ProfessionalsService } from '../../core/services/professionals.service';
import { UserSessionService } from '../../core/services/mock-auth.service';
import { FormDropdownService } from '../../core/services/form-dropdown.service';
import { AuthorizationService } from '../../core/services/authorization.service';
import { NavigationContextService } from '../../core/services/navigation-context.service';
import { AppRole } from '../../core/models/permissions.model';
import { AuthService } from '../../core/services/auth.service';

interface NavItem {
  label?: string;
  icon?: string;
  routerLink?: string;
  command?: () => void;
  disabled?: boolean;
  styleClass?: string;
  title?: string; // Tooltip message
  separator?: boolean; // Flag to render as separator instead of menu item
  statusIcon?: string; // Custom status icon (overrides default getWizardStepStatusIcon)
}

@Component({
  selector: 'app-navbar',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    RouterModule,
    ToolbarModule,
    PanelMenuModule,
    MenuModule,
    TooltipModule,
    ButtonModule,
    ToastModule,
    DialogModule,
  ],
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit {
  protected readonly AppRole = AppRole;

  @HostBinding('class.sidebar-collapsed') get collapsed() {
    return this.isSidebarCollapsed();
  }

  @Output() sidebarCollapsedChange = new EventEmitter<boolean>();

  @Input()
  set collapsedState(value: boolean) {
    this.isSidebarCollapsed.set(value);
  }

  sidebarTitle = signal<string>('Menu Principale');
  items = signal<(NavItem | MenuItem)[]>([]);
  isRegisterSection = signal<boolean>(false);
  isResponsabiliSection = signal<boolean>(false);
  isSubResponsabiliSection = signal<boolean>(false);
  isContractsSection = signal<boolean>(false);
  isWizardRoute = signal<boolean>(false);
  isResponsabileWizard = signal<boolean>(false);
  isContractWizard = signal<boolean>(false);
  isProfessionalsWizard = signal<boolean>(false);
  isSidebarCollapsed = signal<boolean>(false);
  isTrusteePersonalInfo = signal<boolean>(false);
  isQuestionnairesAttachments = signal<boolean>(false);
  isQuestionnaireSection = signal<boolean>(false); // Sezione lista questionari e checklist
  currentUrl = signal<string>(''); // Signal reattivo per l'URL corrente
  checklistAvailable = signal<boolean>(false); // Checklist disponibile solo dopo salvataggio contratto
  showSubmitConfirmDialog = signal<boolean>(false); // Dialog di conferma per l'invio anagrafica

  // Signal per mostrare il tipo di fiduciario in modo leggibile
  professionalType = signal<string | null>(null);

  // Mapping delle tipologie fiduciario
  private professionalTypeLabels = new Map<string, string>();

  // Computed per ottenere il ruolo corrente dell'utente
  currentRole = computed(() => this.authzService.currentUser()?.roles?.[0] || null);

  // Computed per verificare se l'utente è backoffice_professional (controlla entrambe le varianti)
  isBackofficeProfessional = computed(() => {
    const roles = this.authzService.currentUser()?.roles ?? [];
    return (
      roles.includes(AppRole.BACKOFFICE_PROFESSIONAL) ||
      roles.includes('backoffice_professional' as AppRole) ||
      roles.includes('backoffice_professional' as AppRole)
    );
  });

  // Computed per aggiornare automaticamente il menu wizard quando cambiano gli step
  wizardMenuItems = computed<NavItem[]>(() => {
    if (!this.isWizardRoute()) return [];
    const steps = this.wizardStepService.steps();
    return steps.map((step, idx): NavItem => {
      // Usa il campo disabled dello step se definito, altrimenti usa la logica legacy
      const isDisabled =
        step.disabled !== undefined ? step.disabled : step.id > 0 && !steps[step.id - 1]?.completed;

      let styleClass = '';

      if (step.active) {
        styleClass = 'active-wizard-step';
      } else if (step.completed) {
        styleClass = 'completed-wizard-step';
      } else if (isDisabled) {
        styleClass = 'disabled-wizard-step';
      } else {
        styleClass = 'available-wizard-step';
      }

      return {
        label: step.label,
        icon:
          step.icon ||
          (step.completed
            ? 'pi pi-check-circle'
            : step.active
              ? 'pi pi-circle-fill'
              : 'pi pi-circle'),
        // Usa il command dello step se definito, altrimenti usa setStep
        command:
          step.command ||
          (() => {
            if (!isDisabled) {
              this.wizardStepService.setStep(step.id);
            }
          }),
        styleClass: styleClass,
        disabled: isDisabled,
        title: step.tooltipMessage || '', // Usa tooltipMessage come title per il tooltip nativo
      };
    });
  });

  // Computed per il menu wizard dei professionals
  professionalsWizardMenuItems = computed<NavItem[]>(() => {
    if (!this.isProfessionalsWizard()) return [];
    const steps = this.professionalsWizardService.steps().filter(s => !s.hidden);
    const url = this.currentUrl();
    const isOnContractSignature = url.includes('/axa/professionals-contracts');
    
    return steps.map((step): NavItem => {
      // Gli step sono sempre abilitati per consentire la navigazione libera
      const isDisabled = false;

      let styleClass = '';

      // Se siamo su /axa/professionals-contracts, nessuno degli step base deve essere active
      if (isOnContractSignature) {
        styleClass = step.completed ? 'completed-wizard-step' : 'available-wizard-step';
      } else if (step.active) {
        styleClass = 'active-wizard-step';
      } else if (step.completed) {
        styleClass = 'completed-wizard-step';
      } else {
        styleClass = 'available-wizard-step';
      }

      return {
        label: step.label,
        icon:
          step.icon ||
          (step.completed
            ? 'pi pi-check-circle'
            : step.active
              ? 'pi pi-circle-fill'
              : 'pi pi-circle'),
        routerLink: step.routerLink,
        // Usa il command dello step se definito, altrimenti naviga
        command:
          step.command ||
          (() => {
            this.professionalsWizardService.setStep(step.id);
            this.router.navigate([step.routerLink]);
          }),
        styleClass: styleClass,
        disabled: isDisabled,
        title: '', // Nessun tooltip necessario dato che gli step sono sempre accessibili
      };
    });
  });

  // Computed per il menu wizard con aggiunta di "Firma contratto" se nello step CONTRACT_SIGNATURE o CONTRACT_COUNTERSIGNATURE
  professionalsWizardMenuWithContract = computed<NavItem[]>(() => {
    const wizardItems = this.professionalsWizardMenuItems();
    const currentStep = this.professionalsWizardService.currentRegistrationStep();
    const url = this.currentUrl();
    
    // Se lo step corrente è CONTRACT_SIGNATURE o CONTRACT_COUNTERSIGNATURE, aggiungi "Firma il contratto"
    if (
      currentStep === RegistrationStep.CONTRACT_SIGNATURE ||
      currentStep === RegistrationStep.CONTRACT_COUNTERSIGNATURE
    ) {
      const isCompleted = currentStep === RegistrationStep.CONTRACT_COUNTERSIGNATURE;
      const isActive = url.includes('/axa/professionals-contracts');
      
      // Determina lo styleClass: active > completed > available
      // Se sei sulla pagina, mostra sempre come active (bg blu) ma con pallino verde se completato
      let styleClass = 'available-wizard-step';
      if (isActive) {
        styleClass = 'active-wizard-step';
      } else if (isCompleted) {
        styleClass = 'completed-wizard-step';
      }
      
      return [
        ...wizardItems,
        { separator: true }, // Separator line
        {
          label: 'Firma il contratto',
          icon: 'pi pi-file-edit',
          routerLink: '/axa/professionals-contracts',
          command: () => {
            this.router.navigate(['/axa/professionals-contracts']);
          },
          styleClass: styleClass,
          disabled: false,
          title: isCompleted ? 'Contratto firmato e inviato' : 'Scarica, firma e carica il contratto',
          statusIcon: isCompleted ? 'pi pi-check-circle text-green-500' : (isActive ? 'pi pi-circle-fill text-blue-500' : 'pi pi-circle text-400'),
        },
      ];
    }
    
    return wizardItems;
  });

  // Menu items per questionari
  private questionnaireItems: NavItem[] = [
    {
      label: 'Lista Questionari',
      icon: 'pi pi-list',
      routerLink: '/questionnaires',
    },
    {
      label: 'Lista File Checklist',
      icon: 'pi pi-list',
      routerLink: '/list-file-checklist',
    },
  ];

  // Menu items per registro fornitori
  private registerItems: MenuItem[] = [
    {
      label: 'Fornitori',
      icon: 'pi pi-building',
      routerLink: '/axa/suppliers-registry',
      styleClass: 'registro-menu-item',
    },
  ];

  // Menu items per registro fiduciari
  private professionalsRegistryItems: MenuItem[] = [
    {
      label: 'Registro Fiduciari',
      icon: 'pi pi-users',
      routerLink: '/axa/professionals-registry',
      styleClass: 'registro-menu-item',
    },
  ];

  private trusteePersonalInfoItems: MenuItem[] = [
    {
      label: 'Dati Generali',
      icon: 'pi pi-user',
      routerLink: '/axa/professionals-general-data',
      styleClass: 'registro-menu-item',
    },
    {
      label: 'Dati Specifici',
      icon: 'pi pi-building',
      routerLink: '/axa/professionals-specific-data',
      styleClass: 'registro-menu-item',
    },
    {
      label: 'Collaboratori Professionisti e Impiegati Amministrativi',
      icon: 'pi pi-users',
      routerLink: '/axa/professionals-staff-collaborators',
      styleClass: 'registro-menu-item',
    },
    {
      label: 'Questionari e Allegati',
      icon: 'pi pi-file',
      routerLink: '/axa/professionals-questionnaire-attachments',
      styleClass: 'registro-menu-item',
    },
    {
      separator: true,
    },
    {
      label: 'Firma il contratto',
      icon: 'pi pi-file-edit',
      routerLink: '/axa/professionals-contracts',
      styleClass: 'registro-menu-item',
    },
  ];

  // Menu items for questionnaires and attachments configuration
  private questionnairesAttachmentsItems: MenuItem[] = [
    {
      label: 'Gestisci questionari',
      icon: 'pi pi-file-edit',
      routerLink: '/axa/questionnaires-and-attachments',
      styleClass: 'registro-menu-item',
    },
    {
      label: 'Gestisci allegati',
      icon: 'pi pi-paperclip',
      routerLink: '/axa/questionnaires-and-attachments',
      styleClass: 'registro-menu-item',
    },
  ];

  // Menu items per gestione contratti
  private getContractsItems(): MenuItem[] {
    const domainPrefix = this.navigationContext.getDomainPrefix();
    const contextLabel = domainPrefix === 'supplier' ? 'Fornitori' : 'Fiduciari';

    return [
      {
        label: `Contratti ${contextLabel}`,
        icon: 'pi pi-file-edit',
        styleClass: 'registro-menu-item',
        command: () => {
          this.router.navigate([`/axa/${domainPrefix}-contracts-registry`]);
        },
      },
      {
        label: 'Template Documentali',
        icon: 'pi pi-file-word',
        routerLink: `/axa/${domainPrefix}-document-templates`,
        styleClass: 'registro-menu-item',
      },
      {
        label: 'Allegati Riusabili',
        icon: 'pi pi-paperclip',
        routerLink: `/axa/${domainPrefix}-document-attachments`,
        styleClass: 'registro-menu-item',
      },
      {
        label: 'Articoli Riusabili',
        icon: 'pi pi-book',
        routerLink: `/axa/${domainPrefix}-document-articles`,
        styleClass: 'registro-menu-item',
      },
    ];
  }

  // Menu completo per sezione registro (supplier, admin, ecc. - NOT backoffice_professional)
  private completeRegisterItems: MenuItem[] = [
    {
      label: 'Fornitori',
      icon: 'pi pi-building',
      routerLink: '/axa/suppliers-registry',
      styleClass: 'registro-menu-item',
    },
    {
      label: 'Sub-Fornitori',
      icon: 'pi pi-sitemap',
      routerLink: '/axa/suppliers-registry-sub-fornitori',
      styleClass: 'registro-menu-item',
    },
    {
      label: 'Responsabili',
      icon: 'pi pi-users',
      routerLink: '/axa/responsibles-registry',
      styleClass: 'registro-menu-item',
    },
    {
      label: 'Sub-Responsabili',
      icon: 'pi pi-user',
      routerLink: '/axa/sub-responsibles-registry',
      styleClass: 'registro-menu-item',
    },
  ];

  // Computed: verifica se tutti gli step obbligatori del professionals wizard sono completati
  // In network onboarding mode solo step 1 e 2 sono obbligatori
  // In operational location mode step 1 e (se visibile) 3 sono obbligatori
  areRequiredStepsCompleted = computed(() => {
    if (!this.isProfessionalsWizard()) return false;
    const steps = this.professionalsWizardService.steps();
    const step1 = steps.find((s) => s.id === 1);
    // Step 3 (Impiegati Amministrativi e Collaboratori) è obbligatorio quando visibile
    const step3 = steps.find((s) => s.id === 3);
    const staffOk = step3?.hidden ? true : (step3?.completed ?? false);
    if (this.professionalsWizardService.isOperationalLocationOnboarding()) {
      return (step1?.completed ?? false) && staffOk;
    }
    if (this.professionalsWizardService.isNetworkOnboarding()) {
      const step2 = steps.find((s) => s.id === 2);
      return (step1?.completed ?? false) && (step2?.completed ?? false);
    }
    const step0 = steps.find((s) => s.id === 0);
    const step2 = steps.find((s) => s.id === 2);
    return (
      (step0?.completed ?? false) && (step1?.completed ?? false) && (step2?.completed ?? false) && staffOk
    );
  });

  // Signal for submission state
  isSubmittingRegistration = signal<boolean>(false);
  isActivatingNetworkBodyShop = signal<boolean>(false);

  canSubmitRegistration = computed(() => {
    if (!this.isProfessionalsWizard() || this.professionalsWizardService.isNetworkOnboarding()) {
      return false;
    }

    if (!this.areRequiredStepsCompleted()) {
      return false;
    }

    return this.professionalsWizardService.currentRegistrationStep() === RegistrationStep.DATA_INPUT;
  });

  // Computed: messaggio del tooltip per il pulsante Invia Anagrafica
  submitRegistrationTooltip = computed(() => {
    if (!this.areRequiredStepsCompleted()) {
      return "Completa tutti gli step obbligatori (Dati Generali, Dati Specifici, Collaboratori Professionisti e Impiegati Amministrativi, Questionari e Allegati) per abilitare l'invio";
    }

    const currentRegistrationStep = this.professionalsWizardService.currentRegistrationStep();
    if (!currentRegistrationStep) {
      return 'Attendi il caricamento dello stato della registrazione prima di inviare l\'anagrafica';
    }

    if (currentRegistrationStep !== RegistrationStep.DATA_INPUT) {
      return "L'anagrafica risulta gia inviata o non e piu reinviabile da questa schermata";
    }

    if (this.areRequiredStepsCompleted()) {
      return "Clicca per inviare l'anagrafica completa";
    }

    return '';
  });

  // Computed: verifica se siamo in modalità read-only (visualizzazione anagrafica inviata)
  isReadOnlyMode = computed(() => {
    return this.professionalsDataService.isReadOnly();
  });

  constructor(
    public router: Router,
    public wizardStepService: WizardStepService,
    public professionalsWizardService: ProfessionalsWizardService,
    public navigationContext: NavigationContextService,
    private contractSectionsService: ContractSectionsService,
    private checklistService: ChecklistService,
    private professionalsDataService: ProfessionalsDataService,
    private professionalsService: ProfessionalsService,
    private mockAuthService: UserSessionService,
    private formDropdownService: FormDropdownService,
    private authzService: AuthorizationService,
    private messageService: MessageService,
    private authService: AuthService,
  ) {
    // Load professional type labels from dropdown service (only if authenticated)
    if (this.authService.isAuthenticated()) {
      this.formDropdownService
        .getFormDropdownValues('TRUSTEE_CATEGORY', 'TRUSTEE_CATEGORY')
        .subscribe({
          next: (response) => {
            if (response.data) {
              response.data.forEach((item) => {
                this.professionalTypeLabels.set(item.valueKey, item.valueDescription);
              });
            }
          },
          error: (err) => {
            console.error('❌ Error loading professional type labels:', err);
          },
        });

      // Load professional type on initialization
      this.loadProfessionalType();
    }

    // Effect per aggiornare items quando wizardMenuItems cambia (solo per wizard fornitore e contratti)
    effect(() => {
      // Leggi wizardMenuItems PRIMA della condizione per assicurare che diventi una dipendenza dell'effect
      const menuItems = this.wizardMenuItems();
      if (this.isWizardRoute() && !this.isResponsabileWizard() && !this.isProfessionalsWizard()) {
        this.items.set(menuItems);
      }
    });

    // Effect per aggiornare items quando professionalsWizardMenuItems cambia
    effect(() => {
      const menuItems = this.professionalsWizardMenuWithContract();
      if (this.isProfessionalsWizard()) {
        this.items.set(menuItems);
      }
    });
  }

  ngOnInit(): void {
    // Imposta il menu iniziale in base alla route corrente
    this.currentUrl.set(this.router.url);
    this.updateMenuByRoute(this.router.url);
    // Subscribe to checklist availability changes
    this.checklistService.checklistAvailable$.subscribe((available) => {
      this.checklistAvailable.set(available);
    });
    // Ascolta i cambiamenti di route
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl.set(event.urlAfterRedirects);
        this.updateMenuByRoute(event.urlAfterRedirects);
      });
  }

  loadProfessionalType(): void {
    const professionalId = this.mockAuthService.getDemoProfessionalId();

    // ✅ Check if user is authenticated before making API call
    const hasToken = !!localStorage.getItem('access_token');
    if (!hasToken) {
      console.log('⚠️ [Navbar] No access token found, skip loadProfessionalType API call');
      return;
    }

    // ✅ Only load professional type for actual professional users (those with a professional_id in session)
    const role = this.currentRole();
    if (role !== 'professional') {
      console.log('⚠️ [Navbar] Non-professional role, skip loadProfessionalType API call');
      return;
    }

    if (professionalId) {
      this.professionalsService.getProfessionalGeneralData(professionalId).subscribe({
        next: (response) => {
          if (response?.data?.professionalType) {
            const professionalTypeValue = response.data.professionalType;
            const tipologiaCarrozzeria = response.data.tipologiaCarrozzeria;

            // Usa il mapping dinamico dal FormDropdownService
            let label = this.professionalTypeLabels.get(professionalTypeValue);

            // Se è carrozzeria, aggiungi il sottotipo
            if (professionalTypeValue === 'CARROZZERIA' && tipologiaCarrozzeria) {
              label = label
                ? `${label} ${tipologiaCarrozzeria}`
                : `Carrozzeria ${tipologiaCarrozzeria}`;
            }

            this.professionalType.set(label || professionalTypeValue);
          }
        },
        error: (err) => {
          console.error('❌ Error loading professional general data:', err);
          // Fallback al servizio in memoria se l'API fallisce
          this.loadProfessionalTypeFromMemory();
        },
      });
    } else {
      // Nessun professional ID, fallback al servizio in memoria
      this.loadProfessionalTypeFromMemory();
    }
  }

  private loadProfessionalTypeFromMemory(): void {
    const tipologia = this.professionalsDataService.getTipologiaFiduciario();
    const tipologiaCarrozzeria = this.professionalsDataService.getTipologiaCarrozzeria();

    if (tipologia) {
      let label = this.professionalTypeLabels.get(tipologia);

      // Se è carrozzeria, aggiungi il sottotipo
      if (tipologia === 'BODYSHOP' && tipologiaCarrozzeria) {
        label = label ? `${label} ${tipologiaCarrozzeria}` : `Carrozzeria ${tipologiaCarrozzeria}`;
      }

      this.professionalType.set(label || tipologia);
    }
  }

  private updateMenuByRoute(url: string): void {
    // Reset tutti i flag di sezione prima di ricalcolare, così nessun flag rimane
    // "sporco" da una navigazione precedente. Senza questo reset, isProfessionalsWizard
    // rimane true dopo aver visitato il wizard e contamina tutte le pagine successive.
    this.isWizardRoute.set(false);
    this.isResponsabileWizard.set(false);
    this.isContractWizard.set(false);
    this.isProfessionalsWizard.set(false);
    this.isRegisterSection.set(false);
    this.isResponsabiliSection.set(false);
    this.isSubResponsabiliSection.set(false);
    this.isContractsSection.set(false);
    this.isTrusteePersonalInfo.set(false);
    this.isQuestionnairesAttachments.set(false);
    this.isQuestionnaireSection.set(false);

    // Se stiamo navigando su una route NON-NK e il wizard è in stato NK onboarding, ripulisci subito.
    // Questo evita che lo stato NK contamini qualsiasi altra pagina (dashboard, registry, ecc.).
    const isNkRoute = /professionals-(?:specific-data|questionnaire-attachments)\/(NK-[A-Z0-9]+)/.test(url);
    if (!isNkRoute && this.professionalsWizardService.isNetworkOnboarding()) {
      this.professionalsWizardService.resetToDefault();
    }

    // Controlla se la navigazione proviene dal dettaglio fornitore
    const navigationState =
      this.router.getCurrentNavigation()?.extras?.state || (window.history.state as any);
    const isFromSupplierDetail = navigationState?.from === 'supplier-detail';

    if (url.includes('/axa/supplier-registration-wizard')) {
      // Mostra gli step del wizard fornitore
      this.sidebarTitle.set('Nuovo Fornitore');
      this.isWizardRoute.set(true);
      this.isResponsabileWizard.set(false);
      this.items.set(this.wizardMenuItems());
      this.isRegisterSection.set(true);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
    } else if (url.includes('/axa/responsible-registration-wizard')) {
      // Mostra solo Anagrafica Responsible
      this.sidebarTitle.set('Nuovo Responsible');
      this.isWizardRoute.set(true);
      this.isResponsabileWizard.set(true);
      this.items.set([
        {
          label: 'Anagrafica Responsible',
          icon: 'pi pi-user',
          styleClass: 'active-menu-item',
          disabled: false,
        },
      ]);
      this.isRegisterSection.set(true);
      this.isResponsabiliSection.set(true);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
    } else if (url.includes('/axa/sub-responsible-registration-wizard')) {
      // Mostra solo Anagrafica Sub-Responsible
      this.sidebarTitle.set('Nuovo Sub-Responsabile');
      this.isWizardRoute.set(true);
      this.isResponsabileWizard.set(true);
      this.items.set([
        {
          label: 'Anagrafica Sub-Responsabile',
          icon: 'pi pi-user',
          styleClass: 'active-menu-item',
          disabled: false,
        },
      ]);
      this.isRegisterSection.set(true);
      this.isSubResponsabiliSection.set(true);
      this.isResponsabiliSection.set(false);
      this.isContractsSection.set(false);
    } else if (url.includes('/axa/supplier-contract-registration-wizard') ||
               url.includes('/axa/professional-contract-registration-wizard')) {
      // Mostra gli step del wizard contratto
      this.sidebarTitle.set('Nuovo Contratto');
      this.isWizardRoute.set(true);
      this.isResponsabileWizard.set(false);
      this.isContractWizard.set(true);
      this.items.set(this.wizardMenuItems());
      this.isRegisterSection.set(false);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
    } else if (
      (url.includes('/axa/supplier-contracts-registry') ||
        url.includes('/axa/professional-contracts-registry') ||
        url.includes('/axa/supplier-templates-registry') ||
        url.includes('/axa/professional-templates-registry') ||
        url.includes('/axa/supplier-document-templates') ||
        url.includes('/axa/professional-document-templates') ||
        url.includes('/axa/supplier-document-attachments') ||
        url.includes('/axa/professional-document-attachments') ||
        url.includes('/axa/supplier-document-articles') ||
        url.includes('/axa/professional-document-articles') ||
        url.includes('/axa/supplier-contract-templates') ||
        url.includes('/axa/professional-contract-templates') ||
        url.includes('/axa/supplier-contract-sections') ||
        url.includes('/axa/professional-contract-sections')) &&
      !isFromSupplierDetail
    ) {
      // Sezione Gestione Contratti - solo se NON arriviamo dal dettaglio fornitore
      const contextLabel = this.navigationContext.isSupplierContext() ? 'Fornitori' : 'Fiduciari';
      this.sidebarTitle.set(`Gestione Contratti ${contextLabel}`);
      this.isWizardRoute.set(false);
      this.isResponsabileWizard.set(false);
      this.isContractWizard.set(false);
      this.items.set(this.getContractsItems());
      this.isRegisterSection.set(false);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(true);
    } else if (url.includes('/axa/responsibles-registry') || url.includes('/axa/responsible/')) {
      // Sezione Registro Responsabili - mostra menu completo registro
      this.sidebarTitle.set('Gestione Registro Responsabili');
      this.isWizardRoute.set(false);
      this.isResponsabileWizard.set(false);
      this.items.set(this.completeRegisterItems); // Mostra menu completo
      this.isRegisterSection.set(true);
      this.isResponsabiliSection.set(true);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
    } else if (
      url.includes('/axa/sub-responsibles-registry') ||
      url.includes('/axa/sub-responsible/')
    ) {
      // Sezione Registro Sub-Responsabili - mostra menu completo registro
      this.sidebarTitle.set('Gestione Registro Sub-Responsabili');
      this.isWizardRoute.set(false);
      this.isResponsabileWizard.set(false);
      this.items.set(this.completeRegisterItems); // Mostra menu completo
      this.isRegisterSection.set(true);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(true);
      this.isContractsSection.set(false);
    } else if (
      url.includes('/axa/suppliers-registry') ||
      url.includes('/axa/suppliers-registry-sub-fornitori') ||
      url.includes('/axa/supplier/')
    ) {
      // Sezione Registro - mostra menu diversi per backoffice_professional vs altri
      const role = this.currentRole();
      const isBackofficeProfessional = this.isBackofficeProfessional();
      const contextLabel = this.navigationContext.isSupplierContext() ? 'Fornitori' : 'Fiduciari';

      if (isBackofficeProfessional) {
        // Backoffice Professional - show ONLY fiduciari menu
        this.sidebarTitle.set('Gestione Registro Fiduciari');
        this.items.set(this.professionalsRegistryItems);
      } else {
        // Supplier, Admin e altri - show complete register menu (fornitori + responsabili + sub-responsabili)
        this.sidebarTitle.set(`Gestione Registro ${contextLabel}`);
        this.items.set(this.completeRegisterItems);
      }

      this.isWizardRoute.set(false);
      this.isResponsabileWizard.set(false);
      this.isRegisterSection.set(true);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
    } else if (
      url.includes('/axa/professionals-registry') ||
      url.includes('/axa/professionals-registry/')
    ) {
      // Sezione Registro Fiduciari
      this.sidebarTitle.set('Gestione Registro Fiduciari');
      this.isWizardRoute.set(false);
      this.isResponsabileWizard.set(false);
      this.items.set(this.professionalsRegistryItems);
      this.isRegisterSection.set(true);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
    } else if (
      url.includes('/axa/professionals-personal-info') ||
      url.includes('/axa/professionals-general-data') ||
      url.includes('/axa/professionals-specific-data') ||
      url.includes('/axa/professionals-staff-collaborators') ||
      url.includes('/axa/professionals-administrative-staff') ||
      url.includes('/axa/professionals-collaborators') ||
      url.includes('/axa/professionals-contracts') ||
      url.includes('/axa/professionals-questionnaire-attachments') ||
      url.includes('/axa/vrf-questionnaire') ||
      url.includes('/axa/professionals-registration-progress') ||
      (url.includes('/questionnaires/') &&
        url.includes('/run') &&
        !url.includes('/axa/questionnaires/'))
    ) {
      // Wizard "Inserimento Anagrafica" per fiduciari
      const role = this.currentRole();
      
      // backoffice_supplier NON può accedere al wizard fiduciari
      if (role === 'backoffice_supplier') {
        console.warn('⚠️ backoffice_supplier attempting to access professionals wizard');
        this.router.navigate(['/axa/suppliers-dashboard']);
        return;
      }
      
      // professional (utente fiduciario) può accedere:
      // 1. Sempre a /axa/professionals-registration-progress (area personale)
      // 2. Alle pagine del wizard (general-data, specific-data, ecc.) SOLO se sono in modalità modifica
      //    (cioè quando c'è una richiesta di modifica da parte del backoffice)
      // NOTA: Il controllo della modalità modifica viene gestito dai singoli componenti tramite isReadOnly
      // quindi qui permettiamo l'accesso. Se non dovessero poter modificare, vedranno i campi readonly.

      this.sidebarTitle.set('Inserimento Anagrafica');
      this.isWizardRoute.set(true);
      this.isProfessionalsWizard.set(true);
      this.isResponsabileWizard.set(false);
      this.isContractWizard.set(false);

      // Carica lo stato salvato del wizard.
      // Per le route con :id NK- (onboarding NK_BODYSHOP), l'ID è nell'URL e il wizard
      // è già inizializzato dal componente — non sovrascriviamo con loadState(frontofficeId).
      const nkIdMatch = url.match(/professionals-(?:specific-data|questionnaire-attachments)\/(NK-[A-Z0-9]+)/);
      const nkRouteId = nkIdMatch?.[1];
      if (!nkRouteId) {
        // Flusso frontoffice standard: carica stato dal sessionStorage.
        // Se eravamo in sessione NK in-memoria, resetta prima di caricare lo stato standard.
        if (this.professionalsWizardService.isNetworkOnboarding()) {
          this.professionalsWizardService.resetToDefault();
        }
        // Se siamo in modalità sede operativa, non sovrascrivere lo stato già configurato
        if (!this.professionalsWizardService.isOperationalLocationOnboarding()) {
          const professionalId = this.mockAuthService.getDemoProfessionalId();
          this.professionalsWizardService.loadState(professionalId || undefined);
        }
      } else {
        // Route NK-: il NavigationEnd si emette PRIMA che il componente esegua ngOnInit,
        // quindi il wizard service non è ancora stato inizializzato. Lo prepariamo qui
        // sincrono così la navbar mostra subito [Dati Specifici, Questionari e Allegati].
        // Quando il componente completa la HTTP call, initNetworkOnboarding() verrà chiamato
        // di nuovo con i dati reali (networkId, subType) e la navbar si aggiornerà tramite effect.
        if (!this.professionalsWizardService.isNetworkOnboarding() ||
            this.professionalsWizardService.networkOnboardingBodyShopId() !== nkRouteId) {
          this.professionalsWizardService.prepareNKStepVisibility(nkRouteId);
        }
      }

      // Determina lo step corrente in base alla URL
      // Non impostare step per professionals-contracts (pagina separata dal wizard)
      if (!url.includes('professionals-contracts')) {
        let currentStepId = 0;
        if (url.includes('professionals-general-data')) {
          currentStepId = 0;
        } else if (url.includes('professionals-specific-data')) {
          currentStepId = 1;
        } else if (
        url.includes('professionals-questionnaire-attachments') ||
        url.includes('vrf-questionnaire')
      ) {
          currentStepId = 2;
        } else if (
          url.includes('professionals-staff-collaborators') ||
          url.includes('professionals-administrative-staff') ||
          url.includes('professionals-collaborators')
        ) {
          currentStepId = 3;
        }

        // Imposta lo step corrente nel wizard service
        this.professionalsWizardService.setStep(currentStepId);
      }

      // Imposta il menu wizard con aggiunta condizionale di "Firma contratto"
      this.items.set(this.professionalsWizardMenuWithContract());
      this.isRegisterSection.set(false);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
      this.isTrusteePersonalInfo.set(false);
      this.isQuestionnairesAttachments.set(false);
    } else if (url.includes('/axa/questionnaires-and-attachments')) {
      // Sezione Gestione Questionari e Allegati - solo per admin, backoffice_admin, backoffice_professional
      const role = this.currentRole();
      if (role === 'backoffice_supplier') {
        // backoffice_supplier NON può accedere alla gestione questionari
        console.warn('⚠️ backoffice_supplier attempting to access questionnaires configuration');
        this.router.navigate(['/axa/suppliers-dashboard']);
        return;
      }

      this.sidebarTitle.set('Configurazione questionari e allegati');
      this.isWizardRoute.set(false);
      this.isResponsabileWizard.set(false);
      this.items.set(this.questionnairesAttachmentsItems);
      this.isRegisterSection.set(false);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
      this.isTrusteePersonalInfo.set(false);
      this.isQuestionnairesAttachments.set(true);
      this.isQuestionnaireSection.set(false);
    } else if (url.includes('/questionnaires') || url.includes('/list-file-checklist') || url.includes('/create-questionnaire') || url.includes('/create-file-checklist')) {
      // Sezione Lista Questionari e Checklist (include anche pagine di creazione/modifica)
      this.sidebarTitle.set('Gestione Questionari e Checklist');
      this.isWizardRoute.set(false);
      this.isResponsabileWizard.set(false);
      this.items.set(this.questionnaireItems);
      this.isRegisterSection.set(false);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
      this.isTrusteePersonalInfo.set(false);
      this.isQuestionnairesAttachments.set(false);
      this.isQuestionnaireSection.set(true);
    } else {
      this.sidebarTitle.set('Menu Principale');
      this.isWizardRoute.set(false);
      this.isResponsabileWizard.set(false);
      this.items.set(this.questionnaireItems);
      this.isRegisterSection.set(false);
      this.isResponsabiliSection.set(false);
      this.isSubResponsabiliSection.set(false);
      this.isContractsSection.set(false);
      this.isTrusteePersonalInfo.set(false);
      this.isQuestionnairesAttachments.set(false);
      this.isQuestionnaireSection.set(false);
    }
  }

  onItemClick(item: NavItem | MenuItem): void {
    if (!item.disabled && item.command) {
      item.command({ originalEvent: new Event('click'), item });
    }
  }

  navigateToAddEntity(): void {
    const url = this.currentUrl();
    if (url.includes('/axa/responsibles-registry')) {
      this.router.navigate(['/axa/responsible-registration-wizard']);
    } else if (url.includes('/axa/sub-responsibles-registry')) {
      this.router.navigate(['/axa/sub-responsible-registration-wizard']);
    }
  }

  isActiveRoute(route: string): boolean {
    const url = this.currentUrl();
    if (route === '/axa/suppliers-registry') {
      // Attiva per /axa/suppliers-registry e /axa/supplier/:id
      return url === '/axa/suppliers-registry' || url.startsWith('/axa/supplier/');
    }
    if (route === '/axa/professionals-registry') {
      // Attiva per /axa/professionals-registry e /axa/professionals-registry/:id
      return (
        url === '/axa/professionals-registry' || url.startsWith('/axa/professionals-registry/')
      );
    }
    if (route === '/axa/responsibles-registry') {
      // Attiva per /axa/responsibles-registry e /axa/responsible/:id
      return url === '/axa/responsibles-registry' || url.startsWith('/axa/responsible/');
    }
    if (route === '/axa/sub-responsibles-registry') {
      // Attiva per /axa/sub-responsibles-registry e /axa/sub-responsible/:id
      return url === '/axa/sub-responsibles-registry' || url.startsWith('/axa/sub-responsible/');
    }
    return url === route;
  }

  toggleSidebar(): void {
    const newValue = !this.isSidebarCollapsed();
    this.isSidebarCollapsed.set(newValue);
    this.sidebarCollapsedChange.emit(newValue);
  }

  getWizardStepStatusIcon(stepIndex: number): string {
    // Determina quale wizard service usare, escludendo gli step nascosti
    const steps = this.isProfessionalsWizard()
      ? this.professionalsWizardService.steps().filter(s => !s.hidden)
      : this.wizardStepService.steps();

    if (stepIndex >= steps.length) return 'pi pi-circle text-400';

    const step = steps[stepIndex];
    if (step.completed) {
      return 'pi pi-check-circle text-green-500';
    } else if (step.active) {
      return 'pi pi-circle-fill text-blue-500';
    } else {
      return 'pi pi-circle text-400';
    }
  }

  openChecklist(): void {
    this.checklistService.requestOpenChecklist();
  }

  onNewBlockClick(): void {
    this.router.navigate([`/axa/${this.navigationContext.getDomainPrefix()}-contract-sections/new`]);
  }

  /**
   * Shows confirmation dialog before submitting registration
   */
  onSubmitRegistration(): void {
    if (this.isSubmittingRegistration()) {
      return;
    }

    if (!this.areRequiredStepsCompleted()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail:
          "Completa tutti gli step obbligatori (Dati Generali, Dati Specifici, Questionari e Allegati) prima di inviare l'anagrafica.",
        life: 5000,
      });
      return;
    }

    const currentRegistrationStep = this.professionalsWizardService.currentRegistrationStep();
    if (!currentRegistrationStep) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attendere',
        detail: 'Lo stato della registrazione e ancora in caricamento. Riprova tra un istante.',
        life: 5000,
      });
      return;
    }

    if (currentRegistrationStep !== RegistrationStep.DATA_INPUT) {
      this.messageService.add({
        severity: 'info',
        summary: 'Invio non disponibile',
        detail: "L'anagrafica e gia stata inviata oppure e in una fase successiva del workflow.",
        life: 5000,
      });
      return;
    }

    // Show confirmation dialog
    this.showSubmitConfirmDialog.set(true);
  }

  /**
   * Cancels the submit operation and closes the dialog
   */
  cancelSubmitRegistration(): void {
    if (this.isSubmittingRegistration()) {
      return;
    }

    this.showSubmitConfirmDialog.set(false);
  }

  /**
   * Confirms and submits the complete registration from navbar
   */
  confirmSubmitRegistration(): void {
    if (this.isSubmittingRegistration()) {
      return;
    }

    this.isSubmittingRegistration.set(true);

    // Close the dialog
    this.showSubmitConfirmDialog.set(false);

    const currentRegistrationStep = this.professionalsWizardService.currentRegistrationStep();
    if (currentRegistrationStep !== RegistrationStep.DATA_INPUT) {
      this.isSubmittingRegistration.set(false);
      this.messageService.add({
        severity: 'info',
        summary: 'Invio non disponibile',
        detail: "L'anagrafica non e piu in uno stato reinviabile da questa schermata.",
        life: 5000,
      });
      return;
    }


    const professionalId = this.mockAuthService.getDemoProfessionalId();
    if (!professionalId) {
      this.isSubmittingRegistration.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'ID fiduciario non trovato. Riprova.',
        life: 5000,
      });
      return;
    }

    this.professionalsWizardService.completeRegistration(professionalId).subscribe({
      next: (response) => {
        this.isSubmittingRegistration.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Successo',
          detail: 'Anagrafica inviata con successo! La registrazione è stata completata.',
          life: 5000,
        });

        // Navigate to registration progress page after a short delay
        setTimeout(() => {
          this.professionalsWizardService.allowNextNavigationWithoutWarning();
          this.router.navigate(['/axa/professionals-registration-progress']);
        }, 2000);
      },
      error: (error) => {
        this.isSubmittingRegistration.set(false);
        console.error("Errore durante l'invio dell'anagrafica:", error);

        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || "Impossibile inviare l'anagrafica. Riprova più tardi.",
          life: 5000,
        });
      },
    });
  }

  /**
   * Activates a network body shop after its onboarding wizard is completed.
   * Called in place of "Invia Anagrafica" when isNetworkOnboarding() is true.
   */
  onActivateNetworkBodyShop(): void {
    if (!this.areRequiredStepsCompleted()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Completa i Dati Specifici e gli Allegati obbligatori prima di attivare la carrozzeria.',
        life: 5000,
      });
      return;
    }

    const bodyShopId = this.professionalsWizardService.networkOnboardingBodyShopId();
    const networkId = this.professionalsWizardService.networkOnboardingNetworkId();
    if (!bodyShopId || !networkId) {
      this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Dati onboarding mancanti. Riprova.', life: 5000 });
      return;
    }

    this.isActivatingNetworkBodyShop.set(true);
    this.professionalsService.updateBodyShopStatus(bodyShopId, 'REACTIVATE').subscribe({
      next: (res) => {
        this.isActivatingNetworkBodyShop.set(false);
        this.professionalsWizardService.clearNetworkOnboarding();
        this.messageService.add({
          severity: 'success',
          summary: 'Carrozzeria attivata',
          detail: 'La carrozzeria è stata attivata con successo nel network.',
          life: 4000,
        });
        setTimeout(() => {
          this.router.navigate(['/axa/review-professional', networkId]);
        }, 1500);
      },
      error: (error) => {
        this.isActivatingNetworkBodyShop.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Impossibile attivare la carrozzeria. Riprova più tardi.',
          life: 5000,
        });
      },
    });
  }

  /**
   * Sospendi la carrozzeria NK (navbar action — carrozzeria già ACTIVE)
   */
  onSuspendNetworkBodyShop(): void {
    const bodyShopId = this.professionalsWizardService.networkOnboardingBodyShopId();
    const networkId = this.professionalsWizardService.networkOnboardingNetworkId();
    if (!bodyShopId) return;

    this.isActivatingNetworkBodyShop.set(true);
    this.professionalsService.updateBodyShopStatus(bodyShopId, 'SUSPEND').subscribe({
      next: (res) => {
        this.isActivatingNetworkBodyShop.set(false);
        if (res.status) {
          this.professionalsWizardService.networkBodyShopStatus.set('SUSPENDED');
          this.messageService.add({ severity: 'success', summary: 'Carrozzeria sospesa', detail: 'La carrozzeria è stata sospesa.', life: 4000 });
        }
      },
      error: (error) => {
        this.isActivatingNetworkBodyShop.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Impossibile sospendere la carrozzeria.',
          life: 5000,
        });
      },
    });
  }

  /**
   * Elimina la carrozzeria NK dal network (navbar action)
   */
  onDeleteNetworkBodyShop(): void {
    const bodyShopId = this.professionalsWizardService.networkOnboardingBodyShopId();
    const networkId = this.professionalsWizardService.networkOnboardingNetworkId();
    if (!bodyShopId) return;

    this.isActivatingNetworkBodyShop.set(true);
    this.professionalsService.removeBodyShopFromNetwork(bodyShopId).subscribe({
      next: (res) => {
        this.isActivatingNetworkBodyShop.set(false);
        if (res.status) {
          this.professionalsWizardService.clearNetworkOnboarding();
          this.messageService.add({ severity: 'success', summary: 'Carrozzeria eliminata', detail: 'La carrozzeria è stata rimossa dal network.', life: 4000 });
          setTimeout(() => {
            if (networkId) {
              this.router.navigate(['/axa/review-professional', networkId]);
            } else {
              this.router.navigate(['/axa/dashboard']);
            }
          }, 1500);
        }
      },
      error: (error) => {
        this.isActivatingNetworkBodyShop.set(false);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: error?.error?.message || 'Impossibile eliminare la carrozzeria.',
          life: 5000,
        });
      },
    });
  }
}
